import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { listApplications } from "@/api/db";
import {
  FileText, Search, X, MapPin, DollarSign, ChevronRight,
  ChevronLeft, Briefcase, Clock, Building2
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value) {
  if (!value) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

const STATE_ABBR = {
  "Acre": "AC", "Alagoas": "AL", "Amapá": "AP", "Amazonas": "AM",
  "Bahia": "BA", "Ceará": "CE", "Distrito Federal": "DF", "Espírito Santo": "ES",
  "Goiás": "GO", "Maranhão": "MA", "Mato Grosso": "MT", "Mato Grosso do Sul": "MS",
  "Minas Gerais": "MG", "Pará": "PA", "Paraíba": "PB", "Paraná": "PR",
  "Pernambuco": "PE", "Piauí": "PI", "Rio de Janeiro": "RJ", "Rio Grande do Norte": "RN",
  "Rio Grande do Sul": "RS", "Rondônia": "RO", "Roraima": "RR", "Santa Catarina": "SC",
  "São Paulo": "SP", "Sergipe": "SE", "Tocantins": "TO",
};

function getStateAbbreviation(state) {
  if (!state) return null;
  if (STATE_ABBR[state]) return STATE_ABBR[state];
  const normalized = state.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  for (const [name, abbr] of Object.entries(STATE_ABBR)) {
    const nameNorm = name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (nameNorm === normalized) return abbr;
  }
  if (state.length === 2) return state.toUpperCase();
  return state;
}

function formatCityName(city) {
  if (!city) return "";
  const lowerCaseWords = ["de", "da", "das", "do", "dos", "e"];
  return city.toLowerCase().split(" ").map((word, i) =>
    lowerCaseWords.includes(word) && i !== 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
  ).join(" ");
}

/**
 * Combined status logic matching the free platform:
 * - CANCELED → "Desistência"
 * - HIRED → "Contratado"
 * - REJECTED → "Reprovado"
 * - jobStatus CLOSED/COMPLETED → "Encerrada"
 * - else → "Em andamento"
 */
function getCombinedStatus(applicationStatus, jobStatus) {
  if (applicationStatus === "CANCELED") return "Desistência";
  if (applicationStatus === "HIRED") return "Contratado";
  if (applicationStatus === "REJECTED") return "Reprovado";
  if (jobStatus === "CLOSED" || jobStatus === "COMPLETED") return "Encerrada";
  return "Em andamento";
}

function getBadgeStyle(status) {
  switch (status) {
    case "Em andamento":
      return { background: "#FEEDB4", color: "#65541B" };
    case "Encerrada":
      return { background: "#FADBDF", color: "#892E38" };
    case "Desistência":
      return { background: "#E5E9EA", color: "#2E3233" };
    case "Contratado":
      return { background: "#D6F5E6", color: "#008572" };
    case "Reprovado":
      return { background: "#2E3233", color: "#E5E9EA" };
    default:
      return { background: "#E5E9EA", color: "#646C6F" };
  }
}

// ─── Company Logo ─────────────────────────────────────────────────────────────

function CompanyLogo({ logoUrl, name, size = 70 }) {
  const [imgError, setImgError] = useState(false);
  if (logoUrl && !imgError) {
    return (
      <img src={logoUrl} alt={name} onError={() => setImgError(true)}
        className="rounded-full object-contain bg-white flex-shrink-0"
        style={{ width: size, height: size, border: "1px solid #E5E9EA" }} />
    );
  }
  return (
    <div className="rounded-full flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size, background: "#F2F4F4" }}>
      <Building2 className="w-8 h-8" style={{ color: "#99A6AB" }} />
    </div>
  );
}

// ─── Application Card ─────────────────────────────────────────────────────────

function ApplicationCard({ app }) {
  const salary = `${formatCurrency(app.minSalary)} - ${formatCurrency(app.maxSalary)}`;
  const combinedStatus = getCombinedStatus(app.applicationStatus, app.jobStatus);
  const badgeStyle = getBadgeStyle(combinedStatus);

  const jobPositionWithSeniority = app.seniority
    ? `${app.title} ${app.seniority}`
    : app.title;

  const locationStr = app.location?.city && app.location?.state
    ? `${formatCityName(app.location.city)}, ${getStateAbbreviation(app.location.state)}`
    : "Todas as regiões";

  const iconColor = "#2E3233";

  return (
    <div className="rounded-3xl flex flex-col justify-between h-full"
      style={{ background: "rgba(255,255,255,0.7)", border: "1px solid #e2e8f0" }}>
      <div className="flex flex-col gap-4 p-5 md:p-6 h-full">
        {/* Logo + Title + Company */}
        <div className="flex gap-4 items-center">
          <CompanyLogo logoUrl={app.logoUrl} name={app.companyName} size={70} />
          <div className="flex flex-col gap-0.5 min-w-0" style={{ width: "70%" }}>
            <h3 className="font-bold text-sm leading-snug line-clamp-2" style={{ color: "#2E3233" }}>
              {jobPositionWithSeniority}
            </h3>
            <p className="text-sm truncate" style={{ color: "#646C6F" }}>
              {app.isConfidential ? "Empresa confidencial" : app.companyName}
            </p>
          </div>
        </div>

        {/* Contract model */}
        {app.contractModel && (
          <div className="flex items-center gap-2 overflow-hidden">
            <Briefcase className="w-5 h-5 flex-shrink-0" style={{ color: iconColor }} />
            <span className="text-sm truncate" style={{ color: "#646C6F" }}>{app.contractModel}</span>
          </div>
        )}

        {/* Salary */}
        <div className="flex items-center gap-2 overflow-hidden">
          <DollarSign className="w-5 h-5 flex-shrink-0" style={{ color: iconColor }} />
          <span className="text-sm truncate" style={{ color: "#646C6F" }}>{salary}</span>
        </div>

        {/* Job model + Availability side by side */}
        <div className="flex gap-6 items-center">
          {app.jobModel && (
            <div className="flex items-center gap-2 w-1/2 overflow-hidden">
              <FileText className="w-5 h-5 flex-shrink-0" style={{ color: iconColor }} />
              <span className="text-sm truncate" style={{ color: "#646C6F" }}>{app.jobModel}</span>
            </div>
          )}
          {app.availability && (
            <div className="flex items-center gap-2 w-1/2 overflow-hidden">
              <Clock className="w-5 h-5 flex-shrink-0" style={{ color: iconColor }} />
              <span className="text-sm truncate" style={{ color: "#646C6F" }}>{app.availability}</span>
            </div>
          )}
        </div>

        {/* Location */}
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 flex-shrink-0" style={{ color: iconColor }} />
          <span className="text-sm truncate" style={{ color: "#646C6F" }}>{locationStr}</span>
        </div>

        {/* Footer: Badge + Acessar link */}
        <div className="flex justify-center gap-6 w-full pt-2 mt-auto">
          <div className="w-full flex justify-center">
            <span className="text-xs font-medium rounded-full py-1.5 px-3 whitespace-nowrap w-full flex items-center justify-center"
              style={badgeStyle}>
              {combinedStatus}
            </span>
          </div>
          <div className="w-full flex justify-center">
            <Link
              to={createPageUrl("OpportunityDetail") + `?id=${app.jobOpeningId}`}
              className="flex items-center gap-1 text-sm font-medium transition-opacity hover:opacity-70"
              style={{ color: "#00664C" }}
            >
              Acessar <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton Card ───────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="rounded-3xl p-5 md:p-6 animate-pulse" style={{ background: "rgba(255,255,255,0.7)", border: "1px solid #e2e8f0", height: 380 }}>
      <div className="flex gap-4 items-center mb-6">
        <div className="w-[70px] h-[70px] rounded-full" style={{ background: "#E5E9EA" }} />
        <div className="flex-1 space-y-2" style={{ width: "70%" }}>
          <div className="h-5 rounded-sm w-full" style={{ background: "#E5E9EA" }} />
          <div className="h-4 rounded-sm w-3/5" style={{ background: "#E5E9EA" }} />
        </div>
      </div>
      <div className="space-y-6">
        <div className="h-4 rounded-sm w-1/4" style={{ background: "#E5E9EA" }} />
        <div className="h-4 rounded-sm w-full" style={{ background: "#E5E9EA" }} />
        <div className="flex gap-6">
          <div className="h-4 rounded-sm w-1/2" style={{ background: "#E5E9EA" }} />
          <div className="h-4 rounded-sm w-1/2" style={{ background: "#E5E9EA" }} />
        </div>
        <div className="h-4 rounded-sm w-full" style={{ background: "#E5E9EA" }} />
        <div className="flex justify-between">
          <div className="h-5 rounded-sm w-[45%]" style={{ background: "#E5E9EA" }} />
          <div className="h-5 rounded-sm w-[45%]" style={{ background: "#E5E9EA" }} />
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function MyApplications() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pageSize: 20 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter]);

  // Fetch
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const filters = {
      page,
      pageSize: 20,
      search: debouncedSearch || undefined,
      status: statusFilter ? [statusFilter] : undefined,
    };
    listApplications(filters)
      .then(result => { if (!cancelled) setData(result); })
      .catch(err => console.error("Applications load error:", err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, debouncedSearch, statusFilter]);

  const totalPages = Math.ceil(data.total / data.pageSize);

  return (
    <div className="max-w-[1440px] mx-auto w-full relative">
      {/* Header */}
      <h1 className="text-xl md:text-2xl font-bold mb-1" style={{ color: "#2E3233" }}>
        Minhas candidaturas
      </h1>
      <p className="text-sm" style={{ color: "#646C6F" }}>
        Veja o andamento das vagas que você se candidatou!
      </p>

      <div className="h-6" />

      {/* Search + Filters row */}
      <div className="flex flex-col-reverse gap-4 items-center justify-between w-full mb-6 md:flex-row">
        {/* Counter */}
        <div className="flex items-center gap-2 w-full md:w-fit whitespace-nowrap">
          {!loading && (
            <>
              <span className="text-base font-bold" style={{ color: "#00664C" }}>{data.total}</span>
              <span className="text-base" style={{ color: "#2E3233" }}>
                {data.total !== 1 ? "resultados encontrados" : "resultado encontrado"}
              </span>
            </>
          )}
        </div>

        {/* Search + Filter button */}
        <div className="flex flex-col items-center justify-end gap-2 w-full md:flex-row">
          <div className="w-full md:max-w-[350px]">
            <div className="rounded-3xl bg-white flex items-center gap-2 px-4 py-2.5"
              style={{ border: "1px solid #e2e8f0" }}>
              <Search className="w-4 h-4 flex-shrink-0" style={{ color: "#00664C" }} />
              <input
                type="text"
                placeholder="Buscar vagas por nome ou empresa"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="flex-1 text-sm bg-transparent outline-none"
                style={{ color: "#2E3233" }}
              />
              {search && (
                <button onClick={() => setSearch("")}>
                  <X className="w-3.5 h-3.5" style={{ color: "#C8CED0" }} />
                </button>
              )}
            </div>
          </div>

          {/* Status filter as dropdown-style button */}
          <div className="flex flex-wrap gap-1.5 w-full md:w-fit">
            {STATUS_TABS.map(tab => {
              const active = statusFilter === tab.value;
              return (
                <button
                  key={tab.value}
                  onClick={() => setStatusFilter(tab.value)}
                  className="px-3 py-2 rounded-full text-sm font-medium transition-colors"
                  style={{
                    background: active ? "#EAFAF6" : "transparent",
                    color: active ? "#00664C" : "#646C6F",
                    border: active ? "1px solid #00664C" : "1px solid #E5E9EA",
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Grid of cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : data.items.length === 0 ? (
        <div className="w-full flex justify-center items-center flex-col min-h-[400px] text-center">
          <FileText className="w-16 h-16 mb-4" style={{ color: "#E5E9EA" }} />
          <h4 className="text-base font-bold mb-2" style={{ color: "#2E3233" }}>
            {statusFilter || debouncedSearch ? "Não há informações" : "Ainda não há informações"}
          </h4>
          <p className="text-sm max-w-[360px]" style={{ color: "#646C6F" }}>
            {statusFilter || debouncedSearch
              ? "Nenhuma vaga encontrada com os filtros ou palavras-chave selecionados. Experimente ajustar os filtros e tente novamente!"
              : "No momento, não há vagas disponíveis para visualização."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {data.items.map(app => <ApplicationCard key={app.applicationId} app={app} />)}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors"
            style={{
              border: "1px solid #E5E9EA",
              color: page === 1 ? "#C8CED0" : "#00664C",
              cursor: page === 1 ? "default" : "pointer",
            }}
          >
            <ChevronLeft className="w-4 h-4" /> Anterior
          </button>
          <span className="text-sm" style={{ color: "#646C6F" }}>
            Página {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors"
            style={{
              border: "1px solid #E5E9EA",
              color: page === totalPages ? "#C8CED0" : "#00664C",
              cursor: page === totalPages ? "default" : "pointer",
            }}
          >
            Próxima <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="h-16" />
    </div>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_TABS = [
  { value: "", label: "Todas" },
  { value: "NEW,LIKED,INTERVIEW", label: "Em andamento" },
  { value: "HIRED", label: "Contratado" },
  { value: "CANCELED", label: "Desistência" },
  { value: "REJECTED", label: "Reprovado" },
];
